// KML helpers for KMZ-extracted map layers: icon-style hrefs, GroundOverlay descriptors, and
// layer-relative URL resolution. Hand-rolled string parsing (no DOMParser/jQuery) so it runs
// identically in Node tests and the browser — same tokenizer approach as wms-capabilities.mjs.

var XML_ENTITIES = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'" };

function decodeXml(value) {
	return value.replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z]+);/g, function (whole, body) {
		if (body.charAt(0) === "#") {
			var code = body.charAt(1) === "x" || body.charAt(1) === "X"
				? parseInt(body.slice(2), 16)
				: parseInt(body.slice(1), 10);
			return isNaN(code) ? whole : String.fromCharCode(code);
		}
		return Object.prototype.hasOwnProperty.call(XML_ENTITIES, body) ? XML_ENTITIES[body] : whole;
	});
}

var TOKEN = /<!\[CDATA\[([\s\S]*?)\]\]>|<!--[\s\S]*?-->|<\?[\s\S]*?\?>|<(\/)?\s*([A-Za-z0-9_.:-]+)([^>]*?)(\/)?>|([^<]+)/g;

function attrValue(attrs, name) {
	// Anchor on start-of-string or preceding whitespace (not \b) so a namespaced attribute like
	// gx:id="wrong" can't satisfy a lookup for "id" — \b sits right after the colon and would
	// match there before ever reaching the real id="..." attribute.
	var doubleQuoted = new RegExp("(?:^|\\s)" + name + "\\s*=\\s*\"([^\"]*)\"").exec(attrs || "");
	if (doubleQuoted !== null) {
		return decodeXml(doubleQuoted[1]);
	}
	var singleQuoted = new RegExp("(?:^|\\s)" + name + "\\s*=\\s*'([^']*)'").exec(attrs || "");
	return singleQuoted === null ? null : decodeXml(singleQuoted[1]);
}

// { styleId: { href, scale } } for every <Style id="..."> containing an <IconStyle> icon href.
// Styles without an id (e.g. inline StyleMap pairs) are skipped — the map code looks styles up
// by the feature's styleUrl id.
export function parseIconHrefs(xml) {
	if (typeof xml !== "string" || xml === "") {
		return {};
	}
	var result = {};
	var styleId = null;
	var inIconStyle = false;
	var capture = null; // "href" | "scale" while capturing text inside the IconStyle
	var current = null; // { href, scale } accumulator for the open Style
	var match;
	TOKEN.lastIndex = 0;
	while ((match = TOKEN.exec(xml)) !== null) {
		if (match[1] !== undefined || match[6] !== undefined) {
			var textPart = match[1] !== undefined ? match[1] : match[6];
			if (capture !== null && current !== null) {
				current[capture] += textPart;
			}
			continue;
		}
		if (match[3] === undefined) {
			continue;
		}
		var isClose = match[2] === "/";
		var selfClose = match[5] === "/";
		var local = match[3].split(":").pop().toLowerCase();
		if (isClose) {
			if (local === "style" && styleId !== null) {
				if (current !== null && current.href !== "") {
					// isNaN guard (not `|| 1`): an explicit <scale>0</scale> is the KML idiom for
					// "hide this icon" and must survive as 0, not fall back to the default.
					var parsedScale = parseFloat(current.scale);
					result[styleId] = { href: decodeXml(current.href).trim(), scale: isNaN(parsedScale) ? 1 : parsedScale };
				}
				styleId = null;
				current = null;
			} else if (local === "iconstyle") {
				inIconStyle = false;
			} else if (local === capture) {
				capture = null;
			}
		} else if (!selfClose) {
			if (local === "style") {
				styleId = attrValue(match[4], "id");
				current = styleId === null ? null : { href: "", scale: "" };
			} else if (local === "iconstyle" && styleId !== null) {
				inIconStyle = true;
			} else if ((local === "href" || local === "scale") && inIconStyle) {
				capture = local;
			}
		}
	}
	return result;
}

// Descriptors for every complete <GroundOverlay> (href + all four LatLonBox edges). rotation is
// parsed but the map renders it un-rotated for now (documented limitation).
//
// Tracks the open-element stack (like wms-capabilities.mjs's elementStack) so a field is only
// captured from its expected immediate parent: north/south/east/west/rotation only under
// <LatLonBox> (never <LatLonAltBox>, which a <Region> can carry alongside it), href only under
// <Icon>, color/drawOrder only directly under <GroundOverlay>. A style-depth counter suppresses
// all capture while inside an inline <Style> (e.g. <Style><IconStyle><Icon><href>) so its icon
// href can't concatenate onto the overlay's own <Icon><href>.
export function parseGroundOverlays(xml) {
	if (typeof xml !== "string" || xml === "") {
		return [];
	}
	var overlays = [];
	var current = null;
	var capture = null;
	var elementStack = [];
	var styleDepth = 0;
	var match;
	TOKEN.lastIndex = 0;
	while ((match = TOKEN.exec(xml)) !== null) {
		if (match[1] !== undefined || match[6] !== undefined) {
			var textPart = match[1] !== undefined ? match[1] : match[6];
			if (current !== null && capture !== null) {
				current[capture] += textPart;
			}
			continue;
		}
		if (match[3] === undefined) {
			continue;
		}
		var isClose = match[2] === "/";
		var selfClose = match[5] === "/";
		var local = match[3].split(":").pop().toLowerCase();
		if (isClose) {
			if (local === "groundoverlay" && current !== null) {
				var overlay = buildGroundOverlay(current);
				if (overlay !== null) {
					overlays.push(overlay);
				}
				current = null;
				capture = null;
			} else if (local === capture) {
				capture = null;
			}
			if (local === "style" && styleDepth > 0) {
				styleDepth -= 1;
			}
			elementStack.pop();
		} else if (!selfClose) {
			var parent = elementStack.length > 0 ? elementStack[elementStack.length - 1] : null;
			if (local === "groundoverlay") {
				current = { href: "", color: "", draworder: "", north: "", south: "", east: "", west: "", rotation: "" };
				styleDepth = 0;
			} else if (local === "style") {
				styleDepth += 1;
			} else if (current !== null && styleDepth === 0) {
				if (local === "href" && parent === "icon") {
					capture = local;
				} else if ((local === "north" || local === "south" || local === "east" || local === "west" || local === "rotation") && parent === "latlonbox") {
					capture = local;
				} else if ((local === "color" || local === "draworder") && parent === "groundoverlay") {
					capture = local;
				}
			}
			elementStack.push(local);
		}
	}
	return overlays;
}

function buildGroundOverlay(fields) {
	var href = decodeXml(fields.href).trim();
	var north = parseFloat(fields.north);
	var south = parseFloat(fields.south);
	var east = parseFloat(fields.east);
	var west = parseFloat(fields.west);
	if (href === "" || isNaN(north) || isNaN(south) || isNaN(east) || isNaN(west)) {
		return null;
	}
	var opacity = 1;
	var color = fields.color.trim();
	if (/^[0-9a-fA-F]{8}$/.test(color)) {
		// KML colors are aabbggrr; only the alpha byte matters for an image overlay.
		opacity = parseInt(color.substring(0, 2), 16) / 255;
	}
	var rotation = parseFloat(fields.rotation);
	return {
		href: href,
		north: north,
		south: south,
		east: east,
		west: west,
		rotation: isNaN(rotation) ? 0 : rotation,
		opacity: opacity,
		drawOrder: parseInt(fields.draworder, 10) || 0,
	};
}

// Resolves a KML href for a layer. Relative hrefs resolve against the layer's extraction folder
// (baseUrl); site-absolute ("/...") pass through; anything with a scheme or protocol-relative
// "//" is external — allowed only for allowExternal callers (GroundOverlays, where the page CSP
// still governs what actually loads) and only over https. Traversal segments are rejected.
export function resolveLayerHref(href, baseUrl, allowExternal) {
	if (typeof href !== "string") {
		return null;
	}
	var value = href.trim();
	if (value === "") {
		return null;
	}
	if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(value) || value.indexOf("//") === 0) {
		if (allowExternal === true && /^https:\/\//i.test(value)) {
			return value;
		}
		return null;
	}
	if (value.charAt(0) === "/") {
		return value;
	}
	if (typeof baseUrl !== "string" || baseUrl === "") {
		return null;
	}
	if (value.indexOf("./") === 0) {
		value = value.substring(2);
	}
	var segments = value.split("/");
	for (var i = 0; i < segments.length; i++) {
		if (segments[i] === "..") {
			return null;
		}
	}
	return baseUrl + value;
}

// The folder URL for a layer path under /uploads/layers/ ("5/doc.kml" -> "/uploads/layers/5/"),
// or null for flat single-file layers ("5.kml", "temp/{guid}.kml") which have no companion assets.
export function deriveLayerBaseUrl(relativePath) {
	if (typeof relativePath !== "string" || relativePath === "") {
		return null;
	}
	var prefix = "/uploads/layers/";
	var rest = relativePath;
	if (rest.indexOf("temp/") === 0) {
		prefix += "temp/";
		rest = rest.substring(5);
	}
	var lastSlash = rest.lastIndexOf("/");
	if (lastSlash === -1) {
		return null;
	}
	return prefix + rest.substring(0, lastSlash + 1);
}
